import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import { organizationQuery } from '@/api/queries';
import type { Organization } from '@/api/types';
import { isOrgTab, ORG_TABS, paths } from '@/app/paths';
import { Button, Card, initialsOf, Segmented, Tag } from '@/design-system';
import { formatTin, shortName } from '@/lib/format';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { useGlobalActions } from '@/shell/globalActions';
import { LoadError } from '@/shell/LoadError';
import { currentSubscription, orgSubscriptionsQuery, ownerOf, type Subscription } from './api';
import s from './organizations.module.css';

export type OrgContext = { org: Organization; subscription: Subscription | null };

/** The organization and its current subscription, for the tabs. */
export function useOrg(): OrgContext {
  return useOutletContext<OrgContext>();
}

/** /organizations/:orgId/* — the header card (dots + glow), the 7 tabs, the tab content. */
export function OrganizationLayout() {
  const { orgId = '' } = useParams();
  // Child routes are partly static (`sites`), so the tab comes from the path, not a param.
  const tab = useLocation().pathname.split('/')[3];
  const { t } = useTranslation('organizations');
  const { t: ts } = useTranslation('shell');
  const navigate = useNavigate();
  const { openCreateSite } = useGlobalActions();
  const q = useQuery(organizationQuery(orgId));
  const subs = useQuery(orgSubscriptionsQuery(orgId));
  const org = q.data;
  useBreadcrumbs([{ label: ts('nav.organizations'), to: paths.organizations() }, ...(org ? [{ label: org.brand_name }] : [])]);
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!org || subs.isPending) return null;
  const subscription = currentSubscription(subs.data ?? []);
  const period = subscription?.period;
  const owner = ownerOf(org);
  const tags = [
    org.industry,
    org.plan ? (period && period !== 'one_time' ? t('plan_tag', { plan: org.plan.name, period: t(`period.${period as 'yearly' | 'monthly'}`) }) : org.plan.name) : null,
    org.locales.length ? org.locales.map((l) => l.toUpperCase()).join(' / ') : null,
    owner ? t('pm_tag', { pm: shortName(owner.full_name) }) : null,
  ].filter((x): x is string => !!x);
  const brand = org.brand_colors[0];
  const context: OrgContext = { org, subscription };
  return (
    <>
      <Card texture="entity" relative padding={24} className={s.head}>
        {/* The initials tile takes the brand's first colour when research found one. */}
        <div className={s.tile} style={brand ? { background: brand } : undefined}>
          {initialsOf(org.brand_name)}
        </div>
        <div className={s.main}>
          <h1 className={s.title}>{org.brand_name}</h1>
          <div className={s.legal}>
            {org.legal_name && <span>{org.legal_name}</span>}
            {org.tin && <span className={s.tin}>{t('tin', { tin: formatTin(org.tin) })}</span>}
          </div>
          <div className={s.tags}>
            {tags.map((x) => (
              <Tag key={x}>{x}</Tag>
            ))}
          </div>
        </div>
        <div className={s.actions}>
          <Button variant="secondary" icon="scan-search" onClick={() => navigate(paths.organization(org.id, 'research'))}>
            {t('research')}
          </Button>
          <Button icon="plus" onClick={() => openCreateSite(org.id)}>
            {t('sites.create')}
          </Button>
        </div>
      </Card>
      <Segmented
        variant="tabs"
        label={t('tabs_label')}
        value={isOrgTab(tab) ? tab : 'overview'}
        onChange={(v) => navigate(paths.organization(org.id, v))}
        options={ORG_TABS.map((k) => ({ value: k, label: t(`tabs.${k}`), badge: k === 'sites' ? String(org.sites_count ?? 0) : undefined }))}
      />
      <Outlet context={context} />
    </>
  );
}

/** Every tab has its screen now (P6-B02 built the last, Интеграции): an unknown tab goes to Обзор. */
export function PendingOrgTab() {
  return <Navigate to="../overview" replace relative="path" />;
}
